import classNames from 'classnames'
import { endOfMonth, startOfMonth } from 'date-fns'

import { formatValue } from '../../bubbles/utils'
import { BarColorMap, BarLabelMap, BarType } from './constants'
import { ChartData, VolumeChartDataPerTimeframe } from './types'

type ChartTooltipProps = {
  label?: number
  isActive?: boolean
  payload?: Array<{ payload?: ChartData }>
  activeKey: BarType | null
}

function formatDateTitle(date: number) {
  const startDate = startOfMonth(date)
  const endDate = endOfMonth(startDate)

  const month = startDate.toLocaleString('en-US', { month: 'short' })
  const startDay = startDate.getDate()
  const year = startDate.getFullYear()
  const endDay = endDate.getDate()
  return `${month} ${startDay}-${endDay}, ${year}`
}

function formatDataValue(barType: BarType, payload: Partial<VolumeChartDataPerTimeframe> = {}) {
  const tradeCount = barType === BarType.Buy ? payload.buyTransactionCount : payload.saleTransactionCount
  const value = barType === BarType.Buy ? payload.buyAggregated : payload.saleAggregated
  return `${formatValue(value ?? 0)} (${tradeCount ?? 0} Trade${tradeCount === 1 ? '' : 's'})`
}

function BarTooltipLabel({
  activeKey,
  barType,
  payload,
}: {
  activeKey: BarType | null
  barType: BarType
  payload?: VolumeChartDataPerTimeframe
}) {
  return (
    <div
      className={classNames('flex w-64 items-center justify-between space-x-6 whitespace-nowrap py-1', {
        'text-default font-medium': activeKey === barType,
      })}
    >
      <span>{BarLabelMap[barType]}</span>
      <span className="text-right" style={{ color: BarColorMap[barType] }}>
        {formatDataValue(barType, payload)}
      </span>
    </div>
  )
}

export default function ChartTooltip({ isActive, activeKey, payload, label }: ChartTooltipProps) {
  const cursorPayload = payload?.[0]?.payload?.insider

  if (!isActive) return null

  return (
    <div
      className={classNames(
        'relative -top-8 rounded-md border border-secondary bg-primary px-3.5 pb-2.5 pt-3.5 font-sans text-2xs leading-4 text-muted-2',
        {
          invisible: cursorPayload?.buyTransactionCount === 0 && cursorPayload?.saleTransactionCount === 0,
        }
      )}
      style={{ willChange: 'transform' }} // NOTE:(David): this forces webkit to re-render parent chart container when tooltip was rendered out of the bounds
    >
      {label && (
        <div className="mb-1.5">
          <span className="text-sm font-medium">{formatDateTitle(label)}</span>
        </div>
      )}
      <BarTooltipLabel activeKey={activeKey} barType={BarType.Sale} payload={cursorPayload} />
      <BarTooltipLabel activeKey={activeKey} barType={BarType.Buy} payload={cursorPayload} />
    </div>
  )
}
